<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import UserMenu from './UserMenu.vue';

// Same sections as the iPhone app's tabs; Import is the web-only CSV upload (Settings and Sign out live in the user menu).
const links = [
  { to: '/', label: 'Dashboard' },
  { to: '/transactions', label: 'Transactions' },
  { to: '/spending', label: 'Spending' },
  { to: '/investments', label: 'Investments' },
  { to: '/plan', label: 'Plan' },
  { to: '/import', label: 'Import' },
];

const route = useRoute();
const current = computed(() => links.find((l) => l.to === route.path)?.label ?? '');
/** Phone width: the links collapse into a ☰ menu (design system: nav). */
const menuOpen = ref(false);
watch(() => route.path, () => (menuOpen.value = false));
</script>

<template>
  <header class="fx-nav">
    <button class="fx-iconbtn menu-toggle" :aria-expanded="menuOpen" aria-label="Menu" @click="menuOpen = !menuOpen">
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
    </button>
    <RouterLink to="/" class="fx-nav__brand">
      <img src="/app-icon.png" alt="" width="24" height="24" />
      <span>Finances</span>
      <span v-if="current" class="page-name" aria-hidden="true">{{ current }}</span>
    </RouterLink>
    <nav class="fx-nav__links" aria-label="Main">
      <RouterLink v-for="link in links" :key="link.to" :to="link.to" :aria-current="route.path === link.to ? 'page' : undefined">{{ link.label }}</RouterLink>
    </nav>
    <UserMenu />
    <nav v-if="menuOpen" class="phone-menu" aria-label="Main">
      <RouterLink v-for="link in links" :key="link.to" :to="link.to" :aria-current="route.path === link.to ? 'page' : undefined">{{ link.label }}</RouterLink>
    </nav>
  </header>
</template>

<style scoped>
.menu-toggle, .page-name { display: none; }
.phone-menu {
  position: absolute;
  top: var(--nav-height);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: var(--space-3) var(--space-5) var(--space-5);
  background: var(--surface-raised);
  border-bottom: 1px solid var(--border);
  box-shadow: var(--shadow-overlay);
}
.phone-menu a { display: flex; align-items: center; min-height: var(--touch-min); color: var(--ink-muted); font-weight: 600; text-decoration: none; }
.phone-menu a[aria-current='page'] { color: var(--ink); }
@media (max-width: 640px) {
  .fx-nav { gap: var(--space-3); }
  .menu-toggle { display: inline-grid; }
  .page-name { display: inline; color: var(--ink-muted); font-weight: 600; }
  .page-name::before { content: '·'; margin-right: var(--space-3); }
}
@media (min-width: 641px) { .phone-menu { display: none; } }
</style>
